import { useState } from 'react';
import { Wifi, WifiOff, AlertCircle, AlertTriangle } from 'lucide-react';

import { ICON as TYPE_ICON, COLOR as TYPE_COLOR } from '@/shared/constants/assetTypes';

/**
 * Liste des actifs du parc et de leur état de connexion, filtrable.
 *
 * Le serveur sert cette route depuis le portage du tableau de bord, mais aucun écran
 * ne l'appelait : pour retrouver un équipement muet, il fallait passer par la
 * supervision et filtrer à la main.
 *
 * La liste est bornée à 200 lignes après filtrage : au-delà, le tableau devient
 * illisible et la recherche est le bon outil.
 */
const ETATS = {
  connecte: { style: 'bg-emerald-100 text-emerald-800 border-emerald-200', Icone: Wifi, libelle: 'Connecté' },
  deconnecte: { style: 'bg-red-100 text-red-800 border-red-200', Icone: WifiOff, libelle: 'Déconnecté' },
  inconnu: { style: 'bg-neutral-100 text-neutral-700 border-neutral-200', Icone: AlertCircle, libelle: 'Inconnu' },
  erreur: { style: 'bg-amber-100 text-amber-800 border-amber-200', Icone: AlertTriangle, libelle: 'Erreur' },
};

const LIMITE_AFFICHAGE = 200;

function EtatBadge({ etat }) {
  const { style, Icone, libelle } = ETATS[etat] || ETATS.inconnu;
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] px-1.5 py-0.5 rounded border ${style}`}>
      <Icone className="w-2.5 h-2.5" />
      {libelle}
    </span>
  );
}

export default function AssetsStatusPanel({ data, onOuvrirClient }) {
  const [recherche, setRecherche] = useState('');
  const [filtreType, setFiltreType] = useState('tous');
  const [filtreEtat, setFiltreEtat] = useState('tous');

  if (!data) return null;
  const actifs = data.assets || [];
  const parStatut = data.par_statut || {};

  const filtres = actifs.filter((a) => {
    if (filtreType !== 'tous' && a.type !== filtreType) return false;
    if (filtreEtat !== 'tous' && a.statut_connexion !== filtreEtat) return false;
    if (!recherche) return true;
    const terme = recherche.toLowerCase();
    return [a.identifiant_equipement, a.marque, a.modele, a.reference_chantier, a.client_nom, a.client_ville]
      .filter(Boolean)
      .some((valeur) => String(valeur).toLowerCase().includes(terme));
  });
  const affiches = filtres.slice(0, LIMITE_AFFICHAGE);

  return (
    <div className="rounded-lg border border-neutral-200/80 bg-white p-5" data-testid="dashboard-assets-status">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-[16px] font-semibold text-neutral-900 flex items-center gap-2">
            <Wifi className="w-4 h-4 text-neutral-700" />
            Liste des actifs — État de connexion
          </h2>
          <p className="text-[13px] text-neutral-500">
            {data.total} actifs · <span className="text-emerald-700 font-medium">{parStatut.connecte || 0} connectés</span>
            {parStatut.deconnecte > 0 && (
              <> · <span className="text-red-600 font-medium">{parStatut.deconnecte} déconnectés</span></>
            )}
            {parStatut.inconnu > 0 && <> · <span className="text-neutral-500">{parStatut.inconnu} inconnus</span></>}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Rechercher…"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            className="h-8 px-3 text-[14px] border border-neutral-200 rounded-md w-40"
            data-testid="assets-status-search"
          />
          <select
            value={filtreType}
            onChange={(e) => setFiltreType(e.target.value)}
            className="h-8 px-2 text-[14px] border border-neutral-200 rounded-md"
            data-testid="assets-status-type-filter"
          >
            <option value="tous">Tous types</option>
            {[...new Set(actifs.map((a) => a.type))].filter(Boolean).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={filtreEtat}
            onChange={(e) => setFiltreEtat(e.target.value)}
            className="h-8 px-2 text-[14px] border border-neutral-200 rounded-md"
            data-testid="assets-status-status-filter"
          >
            <option value="tous">Tous états</option>
            <option value="connecte">Connectés</option>
            <option value="deconnecte">Déconnectés</option>
            <option value="inconnu">Inconnus</option>
            <option value="erreur">Erreurs</option>
          </select>
        </div>
      </div>

      <div className="max-h-96 overflow-auto -mx-5">
        <table className="w-full text-[16px] min-w-[46rem]">
          <thead className="sticky top-0 bg-white border-b border-neutral-100 z-10">
            <tr className="text-left text-[12.5px] uppercase tracking-wide text-neutral-400">
              <th className="py-2 px-5 font-medium">Type</th>
              <th className="py-2 px-4 font-medium">Marque / Modèle</th>
              <th className="py-2 px-4 font-medium">N° série</th>
              <th className="py-2 px-4 font-medium text-right">kW</th>
              <th className="py-2 px-4 font-medium">Client</th>
              <th className="py-2 px-4 font-medium">Agrégateur</th>
              <th className="py-2 px-4 font-medium">État</th>
            </tr>
          </thead>
          <tbody>
            {affiches.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-[14px] text-neutral-400" data-testid="assets-status-empty">
                  Aucun actif ne correspond aux filtres.
                </td>
              </tr>
            ) : (
              affiches.map((a) => {
                const Icone = TYPE_ICON[a.type] || AlertCircle;
                return (
                  <tr
                    key={a.id}
                    className="border-b border-neutral-50 hover:bg-neutral-50/60 cursor-pointer"
                    onClick={() => onOuvrirClient?.(a.client_id)}
                    data-testid={`asset-status-row-${a.id}`}
                  >
                    <td className="py-2 px-5">
                      <span className={`inline-flex items-center gap-1 text-[13.5px] ${TYPE_COLOR[a.type] || 'text-neutral-500'}`}>
                        <Icone className="w-3.5 h-3.5" /> {a.type}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-[14px] text-neutral-700">
                      {a.marque} <span className="text-neutral-400">{a.modele}</span>
                    </td>
                    <td className="py-2 px-4 font-mono text-[13px] text-neutral-600">
                      {a.identifiant_equipement || '—'}
                    </td>
                    <td className="py-2 px-4 text-[13.5px] text-neutral-700 text-right">{a.puissance_kw || '—'}</td>
                    <td className="py-2 px-4 text-[13.5px] text-neutral-700">
                      {a.client_nom} <span className="text-neutral-400">{a.client_ville}</span>
                    </td>
                    <td className="py-2 px-4 text-[13px] text-neutral-500">{a.aggregateur}</td>
                    <td className="py-2 px-4"><EtatBadge etat={a.statut_connexion} /></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filtres.length > LIMITE_AFFICHAGE && (
        <p className="text-[13px] text-neutral-400 mt-3" data-testid="assets-status-tronque">
          {affiches.length} lignes affichées sur {filtres.length} — affinez la recherche pour voir les autres.
        </p>
      )}
    </div>
  );
}
